import { CloseCircleOutlined } from "@ant-design/icons";
import { Col, Divider, Form, Space } from "antd";
import PrimaryButton from "components/Buttons/PrimaryButton/PrimaryButton";
import SwitchBtn from "components/Buttons/SwitchBtn/SwitchBtn";
import TextInput from "components/Inputs/TextInput/TextInput";
import Selector from "components/Selector/Selector";
import useMessage from "hooks/useMessage";
import { t } from "i18next";
import PageLayout from "layout/PageLayout/PageLayout";
import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getToken } from "redux/features/app/authTokenSlice";
import centralApi from "services/centralApi";
import { API_ENDPOINTS } from "utils/constants";
import {
  CampaignChannelTypeEnum,
  LaunchCampStagUnitTimeEnum,
} from "utils/enums";

const StaggeredSend: FC = () => {
  const loginUser = useSelector((state: any) => state.loginUser.data);
  const { appId } = useSelector((state: any) => state.activeApp);
  const dispatch: any = useDispatch();
  const { showError, showSuccess } = useMessage();

  const features = useSelector((state: any) => state.featuresSetting.data);
  const channelStatus = features[0]?.channel_status;
  const campaignpipeline = useSelector(
    (state: any) => state.featuresSetting?.data[0]?.campaignPipeline
  );

  // Available channels
  const allChannels = useMemo(() => {
    const all = Object.values(CampaignChannelTypeEnum);
    return all
      .filter((key) => key !== CampaignChannelTypeEnum.ALL && (!channelStatus || channelStatus[key]))
      .map((key) => ({ value: key, label: key }));
  }, [channelStatus]);

  // 🔑 State for channel boxes
  const [channelsConfig, setChannelsConfig] = useState([
    {
      channel: "",
      batchSize: { fetchBatch: 0, jobBatch: 0 },
      stag: { active: true, time: 1, t_unit: LaunchCampStagUnitTimeEnum.MINUTES, max_batch: 1, threshold: 0 },
    },
  ]);

  // Load saved values
  useEffect(() => {
    if (campaignpipeline?.channels) {
      const loadedChannels = Object.keys(campaignpipeline.channels);
      const initialValues = loadedChannels.map((ch) => {
        const cfg = campaignpipeline.channels[ch];
        return {
          channel: ch,
          batchSize: { fetchBatch: cfg.batchSize.fetchBatch, jobBatch: cfg.batchSize.jobBatch },
          stag: {
            active: cfg.stag.active,
            time: cfg.stag.time,
            t_unit: cfg.stag.t_unit || LaunchCampStagUnitTimeEnum.MINUTES,
            max_batch: cfg.stag.max_batch,
            threshold: cfg.stag.threshold,
          },
        };
      });
      setChannelsConfig(initialValues);
    }
  }, [campaignpipeline]);

  // Update handlers
  const updateChannel = (index: number, key: string, value: any) => {
    setChannelsConfig((prev) =>
      prev.map((ch, idx) => (idx === index ? { ...ch, [key]: value } : ch))
    );
  };
  const updateBatch = (index: number, key: string, value: number) => {
    setChannelsConfig((prev) =>
      prev.map((ch, idx) =>
        idx === index ? { ...ch, batchSize: { ...ch.batchSize, [key]: value } } : ch
      )
    );
  };
  const updateStag = (index: number, key: string, value: any) => {
    setChannelsConfig((prev) =>
      prev.map((ch, idx) =>
        idx === index ? { ...ch, stag: { ...ch.stag, [key]: value } } : ch
      )
    );
  };

  const addChannel = () => {
    setChannelsConfig((prev) => [
      ...prev,
      {
        channel: "",
        batchSize: { fetchBatch: 0, jobBatch: 0 },
        stag: { active: true, time: 1, t_unit: LaunchCampStagUnitTimeEnum.MINUTES, max_batch: 1, threshold: 0 },
      },
    ]);
  };

  const removeChannel = (index: number) => {
    setChannelsConfig((prev) => prev.filter((_, idx) => idx !== index));
  };

  // Submit
  const handleSubmit = async () => {
    try {
      const payload = {
        campaignPipeline: {
          channels: channelsConfig.reduce((acc, ch) => {
            if (!ch.channel) return acc;
            acc[ch.channel] = {
              batchSize: ch.batchSize,
              stag: ch.stag,
            };
            return acc;
          }, {} as Record<string, any>),
        },
      };
      

      const authToken = await dispatch(getToken()).unwrap();
      await centralApi("POST", API_ENDPOINTS.SAVE_APP_SETTING_URL, {
        args: payload,
        app_id: appId,
        api_key: loginUser.api_key,
        authToken,
      });
      showSuccess(t("sucessfullySavedLabel"));
    } catch (error) {
      showError(t("somethingWrongLabel"));
    }
  };

  return (
    <PageLayout>
      <h6>{t("staggeredSendLabel")}</h6>
      <Divider />

      <Form layout="horizontal" onFinish={handleSubmit}>
        {channelsConfig.map((ch, idx) => (
          <div key={idx} className="border rounded p-3 mb-3 position-relative">
            <CloseCircleOutlined
              className="position-absolute top-0 end-0 p-2"
              onClick={() => removeChannel(idx)}
            />

            {/* Channel */}
            <Col span={12}>
            <Form.Item
              label={t("selectChannelLabel")}
              
              rules={[
                { required: true, message: t("pleaseSelectChannelLabel") as string },
              ]}
            
            >
              <Selector
                value={ch.channel}
                options={allChannels}
                onChange={(val) => updateChannel(idx, "channel", val)}
              />
            </Form.Item>
</Col>
            {/* Batch Size */}
            <h6>{t("batchSizeLabel")}</h6>
            <div className="d-flex gap-2">
              <Form.Item label={t("fetchBatchLabel")}>
                <TextInput
                  type="number"
                  value={ch.batchSize.fetchBatch}
                  onChange={(e) => updateBatch(idx, "fetchBatch", Number(e.target.value))}
                  min={0}
                />
              </Form.Item>
              <Form.Item label={t("jobBatchLabel")}>
                <TextInput
                  type="number"
                  value={ch.batchSize.jobBatch}
                  onChange={(e) => updateBatch(idx, "jobBatch", Number(e.target.value))}
                  min={0}
                />
              </Form.Item>
            </div>

            {/* Staggered */}
            <h6>{t("staggeredLabel")}</h6>
            <Form.Item label={t("activeLabel")}>
              <SwitchBtn
                checked={ch.stag.active}
                onChange={(val) => updateStag(idx, "active", val)}
              />
            </Form.Item>

            <div className="d-flex  gap-2 mt-2">
            <Form.Item
                label={t("maximumBatchPerTimeWindowLabel")}
              >
               <TextInput
                type="number"
                value={ch.stag.max_batch}
                disabled={!ch.stag.active}
                onChange={(e) => updateStag(idx, "max_batch", Number(e.target.value))}
                min={0}
              />
                </Form.Item>
                <Space.Compact className="ms-4">

            <Form.Item  label={t("timeWindowLabel")}>

              <TextInput
                type="number"
                value={ch.stag.time}
                disabled={!ch.stag.active}
                onChange={(e) => updateStag(idx, "time", Number(e.target.value))}
                min={1}
              />
              </Form.Item>

     <Form.Item className="w-50" >

              <Selector
                value={ch.stag.t_unit}
                options={[
                  { label: t("minutesLabel"), value: LaunchCampStagUnitTimeEnum.MINUTES },
                  { label: t("hoursLabel"), value: LaunchCampStagUnitTimeEnum.HOURS },
                  { label: t("dayLabel"), value: LaunchCampStagUnitTimeEnum.DAYS },
                ]}
                disabled={!ch.stag.active}
                onChange={(val) => updateStag(idx, "t_unit", val)}
              />
             
                </Form.Item>
              </Space.Compact>
                <Form.Item
                className="ms-2"
                label={t("thresholdLabel")}
              >
              <TextInput
                type="number"
                value={ch.stag.threshold}
                disabled={!ch.stag.active}
                onChange={(e) => updateStag(idx, "threshold", Number(e.target.value))}
                min={0}
              />
              </Form.Item>
            </div>
          </div>
        ))}

        <PrimaryButton className="me-2" onClick={addChannel}>
          + {t("addChannelLabel")}
        </PrimaryButton>

        <PrimaryButton type="primary" onClick={handleSubmit} className="mt-3">
          {t("saveLabel")}
        </PrimaryButton>
      </Form>
    </PageLayout>
  );
};

export default StaggeredSend;
